import { useLocation } from 'react-router-dom';
import type { Rashi } from '@kundaliai/shared';
import localesJson from '@/content/i18n/locales.json';

/**
 * Locales as config (CLAUDE.md I4). Adding a language means one entry in
 * locales.json plus one dictionary in content/i18n — no page code. The same
 * JSON drives routing, prerender lists, the sitemap and hreflang, so they
 * cannot drift.
 */
export type Locale = 'en' | 'hi' | 'mr' | 'ne';

export interface LocaleConfig {
  code: Locale;
  /** BCP-47, for <html lang> and og:locale. */
  lang: string;
  /** URL prefix: '' for the default language, '/hi' etc. */
  prefix: string;
  nativeName: string;
  hreflang: string;
  /**
   * Whether the whole public site (home, tools, about…) exists in this language,
   * or only the daily pages. Tier-1 languages start with the daily pages and
   * graduate to the full site; routing, sitemap and hreflang follow this flag.
   */
  site: boolean;
  /** Reference place for the panchang (sunrise sets the day). Defaults to Delhi upstream. */
  place?: { lat: number; lon: number; tz: string; label: string };
  signSlugs: Record<Rashi, string>;
}

export const LOCALES = localesJson.locales as LocaleConfig[];
export const DEFAULT_LOCALE: Locale = 'en';
export const LOCALE_CODES = LOCALES.map((l) => l.code);

export const getLocaleConfig = (code: Locale): LocaleConfig => LOCALES.find((l) => l.code === code)!;

/** '/hi/rashifal/mesh' -> 'hi'; '/rashifal' -> 'en'. */
export function localeFromPath(pathname: string): Locale {
  const first = pathname.split('/')[1];
  return (LOCALES.find((l) => l.prefix === `/${first}`)?.code ?? DEFAULT_LOCALE) as Locale;
}

export function useLocale(): Locale {
  return localeFromPath(useLocation().pathname);
}

/** Prefix a canonical (English) path for a locale: href('hi', '/panchang') -> '/hi/panchang'. The homepage is the bare prefix ('/hi'), never '/hi/'. */
export const href = (locale: Locale, path: string): string => {
  const prefix = getLocaleConfig(locale).prefix;
  return path === '/' ? prefix || '/' : `${prefix}${path}`;
};

export const signSlug = (locale: Locale, rashi: Rashi): string => getLocaleConfig(locale).signSlugs[rashi];

export function rashiFromSlug(locale: Locale, slug: string): Rashi | null {
  const map = getLocaleConfig(locale).signSlugs;
  const hit = (Object.keys(map) as Rashi[]).find((r) => map[r] === slug.toLowerCase());
  return hit ?? null;
}

export interface Alternate {
  hreflang: string;
  path: string;
}

/**
 * hreflang set for a page: one entry per locale plus x-default pointing at
 * English. `pathFor` builds the locale-specific path (slugs differ per language).
 */
export function alternatesFor(pathFor: (locale: Locale) => string): Alternate[] {
  const list = LOCALES.map((l) => ({ hreflang: l.hreflang, path: pathFor(l.code) }));
  return [...list, { hreflang: 'x-default', path: pathFor(DEFAULT_LOCALE) }];
}

/** Locales with the full public site, not just the daily pages. */
export const SITE_LOCALES = LOCALES.filter((l) => l.site);

/** Public site paths that exist under every site-enabled locale prefix. */
export const SITE_PATHS: string[] = ['/', '/tools', '/about', '/contact', '/pricing', '/astrology'];
export const DAILY_PATHS: string[] = ['/rashifal', '/panchang'];

export const isSiteLocale = (locale: Locale): boolean => getLocaleConfig(locale).site;

/**
 * Where a link should go from a page in `locale`. Localised destinations keep
 * the prefix; anything not yet translated (blog, legal, the app) goes to the
 * unprefixed English page rather than a 404.
 */
export function localizedPath(locale: Locale, path: string, toolSlugs: readonly string[] = []): string {
  if (locale === DEFAULT_LOCALE) return path;
  const base = path.split(/[?#]/)[0];
  if (DAILY_PATHS.some((p) => base === p || base.startsWith(`${p}/`))) return href(locale, path);
  if (!isSiteLocale(locale)) return path;
  // The localised homepage is the bare prefix ('/hi'), never '/hi/': that is what
  // hreflang, the sitemap and the prerendered file (hi.html) all use.
  if (base === '/') return getLocaleConfig(locale).prefix;
  if (SITE_PATHS.includes(base) || base.startsWith('/astrology/') || toolSlugs.includes(base.replace(/^\//, ''))) return href(locale, path);
  return path;
}

/** hreflang set for a full-site page: site-enabled locales only, plus x-default. */
export function siteAlternatesFor(path: string): Alternate[] {
  return [
    ...SITE_LOCALES.map((l) => ({ hreflang: l.hreflang, path: `${l.prefix}${path === '/' && l.prefix ? '' : path}` || '/' })),
    { hreflang: 'x-default', path },
  ];
}

/**
 * Paths that exist only in English: the reference sections and the blog.
 * Localised pages must not link to them (see the link audit).
 */
export const ENGLISH_ONLY_PREFIXES = ['/nakshatra', '/planet-in-house', '/lagna', '/blog'];

/**
 * Whether `path` is under an English-only section.
 *
 * Prefix matching must respect the path boundary: '/lagna-calculator' and
 * '/nakshatra-calculator' are localised tool pages that merely share an opening
 * string with '/lagna' and '/nakshatra'. A bare startsWith() silently dropped
 * those two links from every localised tool page.
 */
export const isEnglishOnlyPath = (path: string): boolean => {
  const base = path.split(/[?#]/)[0];
  return ENGLISH_ONLY_PREFIXES.some((p) => base === p || base.startsWith(`${p}/`));
};
